import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, Globe, Menu, X } from 'lucide-react';
import { languageNames, supportedLanguages } from '../i18n/config';
import { useAuth } from '../contexts/AuthContext';
import LanguageSelector from './LanguageSelector';
import Footer from './Footer';

const Layout = ({ children, showHeader = true, showFooter = true }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 모바일 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (isMobileMenuOpen && !target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gray-50">
      {showHeader && (
        <>
          {/* Top Header Bar - Full Width Ink Header */}
          <div className="lux-ink border-b border-white/10 text-white py-2">
        <div className="mx-auto max-w-[1280px] px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Social Media Icons - Hidden on mobile */}
            <div className="hidden md:flex items-center space-x-4">
              <a href="#" className="text-white/50 hover:text-[#c8a45e] transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="#" className="text-white/50 hover:text-[#c8a45e] transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="#" className="text-white/50 hover:text-[#c8a45e] transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" className="text-white/50 hover:text-[#c8a45e] transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781h-1.68V5.526h1.68v1.681zm-1.68 9.781c-1.297 0-2.448-.49-3.323-1.297-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297z"/>
                </svg>
              </a>
              <a href="#" className="text-white/50 hover:text-[#c8a45e] transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
            
            {/* Language Selector and Actions */}
            <div className="flex items-center space-x-2 md:space-x-4">
              <LanguageSelector variant="button" />
              {user ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  disabled={loading}
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>{t('auth.logout', 'Logout')}</span>
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    state={{ from: '/dashboard' }}
                    className="text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    {t('authForm.title.signIn', 'Sign In')}
                  </Link>
                  <Link
                    to="/register"
                    state={{ from: '/dashboard' }}
                    className="text-white/80 hover:text-[#c8a45e] transition-colors text-sm font-medium"
                  >
                    {t('authForm.title.signUp', 'Sign Up')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header Menu */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto max-w-[1280px] px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img
                  src="/scanvcf_icon_transparent_pack/scanvcf_icon_square_transparent_32.png"
                  alt="ScanVCF"
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-xl font-bold tracking-tight text-gray-900">ScanVCF</span>
              </Link>
            </div>

            {/* Desktop Navigation Menu */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link 
                to={user ? "/dashboard" : "/login"}
                state={user ? undefined : { from: '/dashboard' }}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t('navigation.dashboard', '대시보드')}
              </Link>
              <Link 
                to={user ? "/card/create" : "/login"}
                state={user ? undefined : { from: '/card/create' }}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t('home.createCard', '명함 추가')}
              </Link>
              <a
                href="https://scanvcf.com/subscription-management"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#96742f] hover:text-[#7a5d24] transition-colors"
              >
                {t('settings.subscriptionManagement', '구독 관리')}
              </a>
              <Link 
                to="/settings"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t('settings.title', '설정')}
              </Link>
              <Link 
                to="/contact"
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                {t('navigation.contact', 'Contact Us')}
              </Link>
            </nav>

            {/* User Actions */}
            <div className="flex items-center space-x-4">
              </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
                </button>
              </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg mobile-menu-container">
            <div className="px-4 py-2 space-y-1">
              <Link
                to={user ? "/dashboard" : "/login"}
                state={user ? undefined : { from: '/dashboard' }}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.dashboard', '대시보드')}
              </Link>
              <Link
                to={user ? "/card/create" : "/login"}
                state={user ? undefined : { from: '/card/create' }}
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('home.createCard', '명함 추가')}
              </Link>
              <a
                href="https://scanvcf.com/subscription-management"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2 text-sm font-semibold text-[#96742f] hover:text-[#7a5d24] hover:bg-[#c8a45e]/10 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('settings.subscriptionManagement', '구독 관리')}
              </a>
              <Link
                to="/settings"
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('settings.title', '설정')}
              </Link>
              <Link
                to="/contact"
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('navigation.contact', 'Contact Us')}
              </Link>
              
              {/* User Actions */}
              <div className="px-3 py-2 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <LanguageSelector variant="mobile" />
                </div>
                {user ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg"
                      disabled={loading}
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {t('auth.logout', 'Logout')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Link
                      to="/login"
                      state={{ from: '/dashboard' }}
                      className="flex items-center justify-center w-full px-4 py-2 text-gray-600 hover:text-gray-900 font-medium border border-gray-300 rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      {t('authForm.title.signIn', 'Sign In')}
                    </Link>
                    <Link
                      to="/register"
                      state={{ from: '/dashboard' }}
                      className="lux-btn-gold flex items-center justify-center w-full px-4 py-2 font-semibold rounded-full"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      {t('authForm.title.signUp', 'Sign Up')}
                    </Link>
                  </div>
                )}
                
                {/* Android App Download Banner */}
                <div className="mt-4">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.scanvcf.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lux-ink border border-[#c8a45e]/30 flex items-center w-full text-white px-4 py-3 rounded-lg hover:opacity-90 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    <div className="flex-1">
                      <div className="font-medium text-sm">Android 앱 다운로드</div>
                      <div className="text-xs text-white/60">모바일에서 더 편리하게</div>
                    </div>
                    <div className="lux-btn-gold px-3 py-1.5 rounded-full text-xs font-semibold">
                      다운로드
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Main Content */}
      <main className="w-full px-2 sm:px-4 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      {showFooter && <Footer />}
    </div>
  );
};

export default Layout; 